// Helper type representing a colors object, with keys e.g. [red1, red2, etc.]
export type Colors<T extends string> = {
  [K in `${T}${number}`]: string;
};

// NOTE: To add additional one-off named colors:
// - Add a hex value below.
// - Add it to `grantedBrandColors` in token-colors.ts.

// Brand colors
export const navyHex = "#002160";
export const lightblueHex = "#B1E8FF";
export const creamHex = "#FBF7F1";
// Other primary colors - these drive additional palettes.
export const grayHex = "#E2E2E2";
export const greenHex = "#A5F7C1";
export const limegreenHex = "#DEF5AD";
export const orangeHex = "#FAC093";
export const redHex = "#FA9393";
// Text colors
export const textPrimaryHex = "#0E173B";
export const textSecondaryHex = "#64625F";
export const textTertiaryHex = "#ADA8A0";
// Background colors
export const backgroundPrimaryHex = "#FFFFFF";
export const backgroundSecondaryHex = "#FEFDFB";
export const backgroundTertiaryHex = "#FBF7F1";
// Fills
export const groupedPrimaryHex = "#FAF6F0";
export const groupedSecondaryHex = "#F3F0E9";
// Strokes
export const strokePrimaryHex = "#CCC7BF";
export const strokeSecondaryHex = "#EBE7E0";
export const strokeTertiaryHex = "#FBF7F1";

// Core palette for the application - utilizes a lot of the core specific colors
// to apply to standard Tamagui color indices.
//
// These values were previously generated dynamically using culori, but are now
// hardcoded to avoid the culori dependency.
export const grantedPalette = [
  "hsl(0, 0%, 100%)",
  "hsl(40, 60%, 99.02%)",
  "hsl(36, 55.56%, 96.47%)",
  "hsl(36, 50%, 96.08%)",
  "hsl(42, 29.41%, 93.33%)",
  "hsl(36.92, 11.3%, 77.45%)",
  "hsl(218.01, 10.4%, 62.38%)",
  "hsl(217.9, 20.15%, 47.65%)",
  "hsl(218.24, 43.54%, 33.24%)",
  "hsl(219.38, 100%, 18.82%)",
  "hsl(219.38, 100%, 18.82%)",
  "hsl(36, 2.56%, 38.24%)",
  "hsl(228, 61.64%, 14.31%)",
];

export const grantedColors: Colors<"granted"> = {
  granted1: grantedPalette[0]!,
  granted2: grantedPalette[1]!,
  granted3: grantedPalette[2]!,
  granted4: grantedPalette[3]!,
  granted5: grantedPalette[4]!,
  granted6: grantedPalette[5]!,
  granted7: grantedPalette[6]!,
  granted8: grantedPalette[7]!,
  granted9: grantedPalette[8]!,
  granted10: grantedPalette[9]!,
  granted11: grantedPalette[10]!,
  granted12: grantedPalette[11]!,
  granted13: grantedPalette[12]!,
};

/**
 * Maps palette colors to the Granted theme by mapping canonical name to index.
 * This is utilized in templates.ts to build the final mapping. By that point,
 * the palette is expanded with additional transparent colors at each end. These
 * override any other things defined in the base template.
 *
 * Note that the other palettes just use the base template - this only applies
 * to the Granted palette specifically.
 *
 * @param bgIndex Indicates the starting index for the background color.
 * @returns
 */
export const grantedTemplate = (bgIndex: number) => ({
  // Tamagui template keys are used for interoperability with standard
  // components, but the indices may differ slightly. See the base template
  // definition in templates.ts. The comment on each line indicates the design
  // system color name that the index corresponds to.
  background: bgIndex, //            backgroundPrimary
  backgroundHover: bgIndex - 1, //   backgroundPrimary (75% opacity)
  backgroundPress: bgIndex + 2, //   backgroundTertiary
  backgroundFocus: bgIndex + 2, //   backgroundTertiary
  borderColor: bgIndex + 5, //       strokePrimary
  borderColorHover: -bgIndex - 3, // navy
  borderColorPress: -bgIndex - 3, // navy
  borderColorFocus: -bgIndex - 3, // navy
  color: -bgIndex, //                textPrimary
  colorHover: -bgIndex - 3, //       navy
  colorPress: -bgIndex, //           textPrimary
  colorFocus: -bgIndex - 3, //       navy
  colorTransparent: -1,
  placeholderColor: -bgIndex - 1, // textSecondary
  outlineColor: -2,

  // Custom Granted-specific keys, only applied to the Granted theme. These are
  // to allow referring to the standard set of colors by name. Note that the
  // branded colors are instead injected in token-colors.ts.
  backgroundSecondary: bgIndex + 1,
  backgroundTertiary: bgIndex + 2,
  groupedPrimary: bgIndex + 3,
  groupedSecondary: bgIndex + 4,
  strokePrimary: bgIndex + 5,
  textSecondary: -bgIndex - 1,
  // Colors not specifically called out, due to having existing/better options:
  // textPrimary: $color
  // backgroundPrimary: $background
});

// Primary palettes - these values were previously generated dynamically using
// culori, but are now hardcoded to avoid the culori dependency.

export const creamPalette = [
  "hsl(223.81, 0%, 100%)",
  "hsl(35.98, 55.7%, 99.6%)",
  "hsl(35.99, 55.67%, 98.98%)",
  "hsl(35.99, 55.64%, 98.23%)",
  "hsl(35.99, 55.6%, 97.39%)",
  "hsl(36, 55.56%, 96.47%)",
  "hsl(36, 22.32%, 90.03%)",
  "hsl(36, 14.64%, 83.69%)",
  "hsl(36, 11.28%, 77.45%)",
  "hsl(36.01, 7.63%, 65.3%)",
  "hsl(36.01, 6.22%, 57.04%)",
  "hsl(36.01, 1.79%, 48.38%)",
  "hsl(36.04, 7.67%, 26.52%)",
];

export const cream: Colors<"cream"> = {
  cream1: creamPalette[0]!,
  cream2: creamPalette[1]!,
  cream3: creamPalette[2]!,
  cream4: creamPalette[3]!,
  cream5: creamPalette[4]!,
  cream6: creamPalette[5]!,
  cream7: creamPalette[6]!,
  cream8: creamPalette[7]!,
  cream9: creamPalette[8]!,
  cream10: creamPalette[9]!,
  cream11: creamPalette[10]!,
  cream12: creamPalette[11]!,
  cream13: creamPalette[12]!,
};

export const grayPalette = [
  "hsl(223.81, 0%, 100%)",
  "hsl(223.81, 0%, 98.71%)",
  "hsl(223.81, 0%, 96.69%)",
  "hsl(223.81, 0%, 94.28%)",
  "hsl(223.81, 0%, 91.57%)",
  "hsl(223.81, 0%, 88.63%)",
  "hsl(223.81, 0%, 82.78%)",
  "hsl(223.81, 0%, 77.01%)",
  "hsl(223.81, 0%, 71.32%)",
  "hsl(223.81, 0%, 60.2%)",
  "hsl(223.81, 0%, 52.62%)",
  "hsl(223.81, 0%, 44.21%)",
  "hsl(223.81, 0%, 24.44%)",
];

export const gray: Colors<"gray"> = {
  gray1: grayPalette[0]!,
  gray2: grayPalette[1]!,
  gray3: grayPalette[2]!,
  gray4: grayPalette[3]!,
  gray5: grayPalette[4]!,
  gray6: grayPalette[5]!,
  gray7: grayPalette[6]!,
  gray8: grayPalette[7]!,
  gray9: grayPalette[8]!,
  gray10: grayPalette[9]!,
  gray11: grayPalette[10]!,
  gray12: grayPalette[11]!,
  gray13: grayPalette[12]!,
};

export const greenPalette = [
  "hsl(223.81, 0%, 100%)",
  "hsl(136.74, 86.72%, 98.03%)",
  "hsl(137.32, 85.98%, 94.88%)",
  "hsl(138.1, 85.17%, 90.94%)",
  "hsl(139.12, 84.37%, 86.27%)",
  "hsl(140.49, 83.67%, 80.78%)",
  "hsl(140.77, 70.06%, 73.76%)",
  "hsl(141.11, 60.58%, 67.19%)",
  "hsl(141.49, 54.35%, 60.68%)",
  "hsl(142.52, 47.08%, 48.55%)",
  "hsl(143.56, 58.97%, 40.26%)",
  "hsl(145.38, 20.72%, 39.02%)",
  "hsl(154.32, 100%, 7.32%)",
];

export const green: Colors<"green"> = {
  green1: greenPalette[0]!,
  green2: greenPalette[1]!,
  green3: greenPalette[2]!,
  green4: greenPalette[3]!,
  green5: greenPalette[4]!,
  green6: greenPalette[5]!,
  green7: greenPalette[6]!,
  green8: greenPalette[7]!,
  green9: greenPalette[8]!,
  green10: greenPalette[9]!,
  green11: greenPalette[10]!,
  green12: greenPalette[11]!,
  green13: greenPalette[12]!,
};

export const lightbluePalette = [
  "hsl(223.81, 0%, 100%)",
  "hsl(198.25, 100%, 98.36%)",
  "hsl(198.17, 100%, 95.77%)",
  "hsl(198.06, 100%, 92.59%)",
  "hsl(197.91, 100%, 88.9%)",
  "hsl(197.69, 100%, 84.71%)",
  "hsl(197.65, 81.32%, 78.57%)",
  "hsl(197.59, 68.54%, 72.62%)",
  "hsl(197.53, 60.36%, 66.74%)",
  "hsl(197.37, 47.47%, 55.42%)",
  "hsl(197.21, 45.32%, 47.71%)",
  "hsl(196.93, 17.13%, 41.59%)",
  "hsl(194.54, 100%, 17.67%)",
];

export const lightblue: Colors<"lightblue"> = {
  lightblue1: lightbluePalette[0]!,
  lightblue2: lightbluePalette[1]!,
  lightblue3: lightbluePalette[2]!,
  lightblue4: lightbluePalette[3]!,
  lightblue5: lightbluePalette[4]!,
  lightblue6: lightbluePalette[5]!,
  lightblue7: lightbluePalette[6]!,
  lightblue8: lightbluePalette[7]!,
  lightblue9: lightbluePalette[8]!,
  lightblue10: lightbluePalette[9]!,
  lightblue11: lightbluePalette[10]!,
  lightblue12: lightbluePalette[11]!,
  lightblue13: lightbluePalette[12]!,
};

export const limegreenPalette = [
  "hsl(223.81, 0%, 100%)",
  "hsl(81.37, 80.34%, 98.06%)",
  "hsl(80.98, 79.85%, 95%)",
  "hsl(80.49, 79.31%, 91.25%)",
  "hsl(79.89, 78.76%, 86.9%)",
  "hsl(79.17, 78.26%, 81.96%)",
  "hsl(79.02, 62.98%, 74.46%)",
  "hsl(78.85, 53.39%, 67.56%)",
  "hsl(78.66, 47.22%, 60.75%)",
  "hsl(78.19, 40.45%, 48.36%)",
  "hsl(77.74, 50%, 40.06%)",
  "hsl(77.04, 16.19%, 41%)",
  "hsl(72.49, 100%, 9.43%)",
];

export const limegreen: Colors<"limegreen"> = {
  limegreen1: limegreenPalette[0]!,
  limegreen2: limegreenPalette[1]!,
  limegreen3: limegreenPalette[2]!,
  limegreen4: limegreenPalette[3]!,
  limegreen5: limegreenPalette[4]!,
  limegreen6: limegreenPalette[5]!,
  limegreen7: limegreenPalette[6]!,
  limegreen8: limegreenPalette[7]!,
  limegreen9: limegreenPalette[8]!,
  limegreen10: limegreenPalette[9]!,
  limegreen11: limegreenPalette[10]!,
  limegreen12: limegreenPalette[11]!,
  limegreen13: limegreenPalette[12]!,
};

export const navyPalette = [
  "hsl(223.81, 0%, 100%)",
  "hsl(217.64, 28.4%, 90.23%)",
  "hsl(217.67, 27.55%, 75.29%)",
  "hsl(217.79, 26.38%, 57.84%)",
  "hsl(218.13, 39.03%, 38.91%)",
  "hsl(219.38, 100%, 18.82%)",
  "hsl(220.31, 100%, 18.76%)",
  "hsl(221.56, 100%, 18.52%)",
  "hsl(223.19, 100%, 18.46%)",
  "hsl(227.94, 100%, 18.08%)",
  "hsl(232.66, 100%, 17.99%)",
  "hsl(238.08, 95%, 6.81%)",
  "hsl(249.82, 100%, 10.51%)",
];

export const navy: Colors<"navy"> = {
  navy1: navyPalette[0]!,
  navy2: navyPalette[1]!,
  navy3: navyPalette[2]!,
  navy4: navyPalette[3]!,
  navy5: navyPalette[4]!,
  navy6: navyPalette[5]!,
  navy7: navyPalette[6]!,
  navy8: navyPalette[7]!,
  navy9: navyPalette[8]!,
  navy10: navyPalette[9]!,
  navy11: navyPalette[10]!,
  navy12: navyPalette[11]!,
  navy13: navyPalette[12]!,
};

export const orangePalette = [
  "hsl(223.81, 0%, 100%)",
  "hsl(25.41, 98.66%, 97.62%)",
  "hsl(25.53, 97.04%, 93.85%)",
  "hsl(25.69, 95.18%, 89.23%)",
  "hsl(25.9, 93.19%, 83.89%)",
  "hsl(26.21, 91.15%, 77.84%)",
  "hsl(26.28, 82.85%, 72.28%)",
  "hsl(26.36, 74.38%, 66.83%)",
  "hsl(26.45, 68.27%, 61.43%)",
  "hsl(26.71, 56.23%, 50.89%)",
  "hsl(26.99, 64.42%, 43.64%)",
  "hsl(27.52, 24.99%, 36.97%)",
  "hsl(31.22, 100%, 14.55%)",
];

export const orange: Colors<"orange"> = {
  orange1: orangePalette[0]!,
  orange2: orangePalette[1]!,
  orange3: orangePalette[2]!,
  orange4: orangePalette[3]!,
  orange5: orangePalette[4]!,
  orange6: orangePalette[5]!,
  orange7: orangePalette[6]!,
  orange8: orangePalette[7]!,
  orange9: orangePalette[8]!,
  orange10: orangePalette[9]!,
  orange11: orangePalette[10]!,
  orange12: orangePalette[11]!,
  orange13: orangePalette[12]!,
};

export const redPalette = [
  "hsl(223.81, 0%, 100%)",
  "hsl(2.41, 100%, 97.74%)",
  "hsl(2.13, 100%, 94.09%)",
  "hsl(1.69, 100%, 89.48%)",
  "hsl(1.03, 97.78%, 84.05%)",
  "hsl(0, 91.15%, 77.84%)",
  "hsl(359.8, 90.11%, 74.56%)",
  "hsl(359.58, 83.91%, 70.72%)",
  "hsl(359.31, 79%, 66.88%)",
  "hsl(358.63, 65.23%, 58.15%)",
  "hsl(357.96, 57.5%, 51.87%)",
  "hsl(356.86, 26.63%, 33.91%)",
  "hsl(348.14, 100%, 21.78%)",
];

export const red: Colors<"red"> = {
  red1: redPalette[0]!,
  red2: redPalette[1]!,
  red3: redPalette[2]!,
  red4: redPalette[3]!,
  red5: redPalette[4]!,
  red6: redPalette[5]!,
  red7: redPalette[6]!,
  red8: redPalette[7]!,
  red9: redPalette[8]!,
  red10: redPalette[9]!,
  red11: redPalette[10]!,
  red12: redPalette[11]!,
  red13: redPalette[12]!,
};
